<template>
  <section>
    <h2>Files</h2>

    <ol class="my-4" v-if="files.length > 0">
      <li v-for="(file, index) in files" :key="index">
        <h3 :class="$vuetify.breakpoint.lg ? 'd-flex' : ''">
          {{ file.name }}
          <v-spacer class="mb-1"></v-spacer>

          <v-btn
            x-small
            outlined
            color="teal"
            class="mr-1 mb-1"
            :href="file.location"
          >
            Download
          </v-btn>
          <v-btn
            x-small
            outlined
            color="red"
            class="mr-1 mb-1"
            @click="deleteManuscriptFile(file._id)"
          >
            Remove
          </v-btn>
        </h3>
        <address>
          <p v-if="file.description">{{ file.description }}</p>
          <p>{{ formatDate(file.createdAt) }}</p>
        </address>
      </li>
    </ol>
    <p class="my-4" v-else>
      No file upload for this manuscript. Please note that the
      <u>cover letter</u> and <u>manuscript</u> files
      <b class="red--text">MUST</b> be attached before you can submit.
      Supplementary file is optional.
    </p>

    <v-expansion-panels accordion v-if="availableUploadTypes.length > 0">
      <v-expansion-panel>
        <v-expansion-panel-header :disabled="!active" color="green lighten-4">
          <span :class="!active ? 'red--text text--darken-2' : ''">
            {{
              active
                ? "Upload manuscript files"
                : "Please add authors before uploading files"
            }}
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content expanded>
          <v-form
            v-if="canUpload('COVER-LETTER')"
            class="mr-2 mt-4"
            ref="uploadCoverLetter"
            v-model="uploadCoverLetterFormValid"
            @submit.prevent="uploadCoverLetter"
          >
            <h3 class="text-center mb-5">Cover letter*</h3>

            <v-file-input
              outlined
              show-size
              v-model="coverFile"
              label="Select cover letter file *"
              :rules="rules.requiredAttachment"
              :accept="wordOrPDF" 
              hint="Click UPLOAD COVER LETTER after selecting file"
              persistent-hint
              class="mb-3"
              prepend-icon="mdi-file-document"
            ></v-file-input>

            <v-text-field
              outlined 
              v-model="coverDescription"
              prepend-icon="mdi-card-text-outline"
              label="Additional information (optional)"
            ></v-text-field>

            <v-btn
              large
              type="submit"
              color="green lighten-3"
              class="ml-8 mb-2"
              :disabled="
                !manuscript || !active || loading || !uploadCoverLetterFormValid
              "
              :loading="loading"
            >
              <v-icon left>mdi-tray-arrow-up</v-icon> Upload cover letter
            </v-btn>

            <v-divider class="my-5"></v-divider>
          </v-form>

          <v-form
            v-if="canUpload('MAIN-MANUSCRIPT')"
            class="mr-2 mt-4"
            ref="uploadMainManuscript"
            v-model="uploadMainManuscriptFormValid"
            @submit.prevent="uploadMainManuscript"
          >
            <h3 class="text-center mb-5">Main Manuscript*</h3>

            <v-file-input
              outlined
              show-size
              v-model="manuscriptFile"
              label="Select main manuscript file *"
              :rules="rules.requiredAttachment"
              :accept="wordOnly"              
              hint="Click UPLOAD MAIN MANUSCRIPT after selecting file"
              persistent-hint
              class="mb-3"
              prepend-icon="mdi-file-word"
            ></v-file-input>

            <v-text-field
              outlined
              v-model="manuscriptDescription"
              prepend-icon="mdi-card-text-outline"
              label="Additional information (optional)"
            ></v-text-field>

            <v-btn
              large
              type="submit"
              color="green lighten-3"
              class="ml-8 mb-2"
              :disabled="
                !manuscript ||
                !active ||
                loading ||
                !uploadMainManuscriptFormValid
              "
              :loading="loading"
            >
              <v-icon left>mdi-tray-arrow-up</v-icon> Upload main manuscript
            </v-btn>

            <v-divider class="my-5"></v-divider>
          </v-form>

          <v-form
            v-if="canUpload('SUPPLEMENTARY')"
            class="mr-2 mt-4"
            ref="uploadSupplementary"
            v-model="uploadSupplementaryFormValid"
            @submit.prevent="uploadSupplementary"
          >
            <h3 class="text-center mb-5">Supplementary File (optional)</h3>

            <v-file-input
              outlined
              show-size
              v-model="supplementaryFile"
              label="Select supplementary file"
              :rules="rules.requiredAttachment"
              :accept="wordOrPDF"
              prepend-icon="mdi-file-word"
            ></v-file-input>

            <v-text-field
              outlined
              v-model="supplementaryDescription"
              prepend-icon="mdi-card-text-outline"
              label="Additional information"
            ></v-text-field>

            <v-btn
              large
              type="submit"
              color="green lighten-3"
              class="ml-8 mb-2"
              :disabled="
                !manuscript ||
                !active ||
                loading ||
                !uploadSupplementaryFormValid
              "
              :loading="loading"
            >
              <v-icon left>mdi-tray-arrow-up</v-icon> Upload Supplementary file
            </v-btn>
          </v-form>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </section>
</template>

<script>
import { required, attachment } from "../../../tools/rules";
import { formatDate } from "../../../tools/format";

export default {
  props: {
    manuscript: {
      type: String,
    },
    active: {
      type: Boolean,
      default: false,
    },
    files: {
      type: Array,
      dafault: () => [],
    },
  },

  data: () => ({
    uploadFilesFormValid: true,
    uploadCoverLetterFormValid: true,
    uploadMainManuscriptFormValid: true,
    uploadSupplementaryFormValid: true,

    coverFile: null,
    coverDescription: null,

    manuscriptFile: null,
    manuscriptDescription: null,

    supplementaryFile: null,
    supplementaryDescription: null,

    loading: false,
    file: null,
    name: null,
    description: null,

    rules: {
      required,
      requiredAttachment: [...required, ...attachment],
    },
    wordOnly:
      "application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    wordOrPDF:
      "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    uploadTypes: ["MAIN-MANUSCRIPT", "COVER-LETTER", "SUPPLEMENTARY"],
  }),

  computed: {
    availableUploadTypes() {
      const alreadyUploaded = [...this.files].map((f) => f.name);
      return [...this.uploadTypes].filter((t) => !alreadyUploaded.includes(t));
    },
  },

  methods: {
    formatDate,
    resetFile() {
      this.$refs.uploadFileForm.reset();
    },
    canUpload(type) {
      const alreadyUploaded = [...this.files].map((f) => f.name);
      return alreadyUploaded.indexOf(type) == -1;
    },
    uploadCoverLetter() {
      this.uploadFiles(this.coverFile, "COVER-LETTER", this.coverDescription);
    },
    uploadMainManuscript() {
      this.uploadFiles(
        this.manuscriptFile,
        "MAIN-MANUSCRIPT",
        this.manuscriptDescription
      );
    },
    uploadSupplementary() {
      this.uploadFiles(
        this.supplementaryFile,
        "SUPPLEMENTARY",
        this.supplementaryDescription
      );
    },
    async uploadFiles(file, name, description) {
      if (!this.manuscript) return;

      this.loading = true;

      let formData = new FormData();
      formData.append("file", file);
      formData.append("name", name);
      description && formData.append("description", description);

      this.$axios
        .post(`/manuscript/${this.manuscript}/files`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then(() => {
          this.$emit("updated");
        })
        .catch(() => {
          this.$bus.$emit("snackbar", {
            type: "error",
            message: "Could not upload file",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async deleteManuscriptFile(file) {
      if (!this.manuscript) return;

      this.loading = true;

      this.$axios({
        method: "DELETE",
        url: `/manuscript/${this.manuscript}/files/${file}`,
      })
        .then(() => {
          this.$bus.$emit("snackbar", {
            type: "success",
            message: "File removed",
          });

          this.$emit("updated");
        })
        .catch(() => {
          this.$bus.$emit("snackbar", {
            type: "error",
            message: "Could not remove file",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style>
</style>

<style lang="scss" scoped>
address {
  p {
    margin: 0.25em 0;
    padding: 0;
  }
}

li {
  padding: 1em 0;
}

li + li {
  border-top: 1px solid #dddddd;
}
</style>
